import React, { useState, useEffect, useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Send, Mail, FileText, Search, AlertTriangle, CheckCircle2, Stethoscope, Sparkles, Paperclip } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

// Referral composer + review. New draft → save → live PDF preview + Send (email specialist / WhatsApp
// patient). Nothing auto-sends; the referral is filed to the digital file on send. A sent letter is
// read-only. The letter is signed under a NAMED referring dentist (chosen here), never hardcoded.
export default function ReferralCompose(props) {
  const { patient = {}, referral = null, providers = [], suggested_provider_id = null,
          specialists = [], documents = [], disciplines = [], types = [], urgencies = [],
          new_referral_path, ai_draft_path } = props

  const sent = referral?.status === 'sent'
  const [busy, setBusy] = useState(false)
  const [aiBusy, setAiBusy] = useState(false)
  const [aiNote, setAiNote] = useState(null)

  const [form, setForm] = useState({
    provider_id: referral?.provider_id || suggested_provider_id || providers[0]?.id || '',
    specialist_id: referral?.specialist_id || '',
    recipient_name: referral?.recipient_name || '',
    // KEY (dropdown); derive from the linked directory specialist when reviewing an existing referral.
    recipient_discipline: specialists.find((s) => String(s.id) === String(referral?.specialist_id))?.discipline || '',
    recipient_practice: referral?.recipient_practice || '',
    recipient_email: referral?.recipient_email || '',
    recipient_phone: referral?.recipient_phone || '',
    recipient_address: referral?.recipient_address || '',
    recipient_hpcsa: '',
    referral_type: referral?.referral_type || types[0]?.key || 'consultation',
    reason: referral?.reason || '',
    clinical_summary: referral?.clinical_summary || '',
    relevant_history: referral?.relevant_history || '',
    teeth: (referral?.teeth || []).join(', '),
    urgency: referral?.urgency || 'routine',
    urgency_reason: referral?.urgency_reason || '',
    patient_informed: referral?.patient_informed || false,
    attachment_document_ids: referral?.attachment_document_ids || [],
    ai_drafted: referral?.ai_drafted || false,
  })
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const teethArray = () => form.teeth.split(/[,\s]+/).map((t) => t.trim()).filter(Boolean)

  // ── Specialist type-ahead ────────────────────────────────────────────────
  const [specQuery, setSpecQuery] = useState('')
  const [specResults, setSpecResults] = useState(specialists)
  const [showSpec, setShowSpec] = useState(false)
  const specBox = useRef(null)
  useEffect(() => {
    const t = setTimeout(() => {
      const q = specQuery.trim()
      if (!q) { setSpecResults(specialists.slice(0, 25)); return }
      fetch(`/specialists?q=${encodeURIComponent(q)}`, { headers: { Accept: 'application/json' } })
        .then((r) => r.ok ? r.json() : []).then(setSpecResults).catch(() => {})
    }, 200)
    return () => clearTimeout(t)
  }, [specQuery]) // eslint-disable-line
  useEffect(() => {
    const onClick = (e) => { if (specBox.current && !specBox.current.contains(e.target)) setShowSpec(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])
  const pickSpecialist = (s) => {
    setForm((f) => ({ ...f, specialist_id: s.id, recipient_name: s.name, recipient_discipline: s.discipline || '',
      recipient_practice: s.practice_name || '', recipient_email: s.email || '', recipient_phone: s.phone || '',
      recipient_address: s.address || '', recipient_hpcsa: s.hpcsa_number || '' }))
    setShowSpec(false); setSpecQuery('')
  }
  const onRecipientNameChange = (v) => setForm((f) => ({ ...f, recipient_name: v, specialist_id: '' }))

  // ── Draft with AI (human-triggered; grounded in this patient's records) ──
  const draftWithAi = async () => {
    setAiBusy(true); setAiNote(null)
    try {
      const res = await fetch(ai_draft_path, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json' },
        body: JSON.stringify({ teeth: teethArray(), referral_type: form.referral_type, recipient_discipline: form.recipient_discipline }),
      })
      if (!res.ok) throw new Error('draft request failed')
      const d = await res.json()
      setForm((f) => ({ ...f,
        reason: d.reason || f.reason,
        clinical_summary: d.clinical_summary || f.clinical_summary,
        relevant_history: d.relevant_history || f.relevant_history,
        ai_drafted: !!d.ai_drafted }))
      setAiNote(d.source === 'ai'
        ? { kind: 'ai', text: "Drafted from this patient's records. Read it and edit before sending — you are the author." }
        : { kind: 'fallback', text: `Built directly from the records; the AI draft was not used${(d.violations || []).length ? ` (${d.violations.join('; ')})` : ''}.` })
    } catch {
      setAiNote({ kind: 'error', text: 'Could not draft right now — type the referral manually.' })
    } finally { setAiBusy(false) }
  }

  const toggleDoc = (id, on) => setForm((f) => ({ ...f,
    attachment_document_ids: on ? [...f.attachment_document_ids, id] : f.attachment_document_ids.filter((x) => x !== id) }))

  const payload = () => ({
    ...form,
    teeth: teethArray(),
    // Send a sentinel when nothing is ticked, so an empty array isn't dropped and the server can clear it.
    attachment_document_ids: form.attachment_document_ids.length ? form.attachment_document_ids : [''],
  })

  const save = () => {
    setBusy(true)
    const opts = { preserveScroll: true, onFinish: () => setBusy(false) }
    if (referral) router.patch(referral.update_path, payload(), opts)
    else router.post(new_referral_path, payload(), opts)
  }
  const doSend = (path, channel) => {
    if (!window.confirm(`Send this referral ${channel}? It will be filed to the patient's digital file.`)) return
    setBusy(true)
    router.post(path, {}, { onFinish: () => setBusy(false) })
  }

  const referringProvider = providers.find((p) => String(p.id) === String(form.provider_id))
  const urgent = form.urgency === 'urgent' || form.urgency === 'emergency'
  const blockers = referral?.send_blockers || []

  const label = 'block text-xs font-medium text-brand-muted mb-1'
  const input = 'w-full rounded-lg border border-brand-border bg-white px-3 py-2 text-sm text-brand-ink focus:border-brand-primary focus:outline-none'

  return (
    <DashboardLayout>
      <Link href={`/patients/${patient.id}/file`} className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink">
        <ArrowLeft size={14} /> Digital file
      </Link>

      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Stethoscope size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">
            {sent ? 'Referral letter' : referral ? 'Review & send referral' : 'New referral letter'}
          </h1>
          <p className="text-sm text-brand-muted">
            {patient.name}{patient.dob ? ` · ${patient.dob}` : ''}{referral?.reference_number ? ` · ${referral.reference_number}` : ''}
          </p>
        </div>
        {sent && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-700">
            <CheckCircle2 size={15} /> Sent{referral.delivery_channel ? ` · ${referral.delivery_channel}` : ''}
          </span>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ── Form ─────────────────────────────────────────────── */}
        <div className={cn('space-y-5', sent && 'pointer-events-none opacity-60')}>
          {/* Referring dentist */}
          <div className="rounded-xl border border-brand-border bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold text-brand-ink">Referring dentist</h2>
            <select className={input} value={form.provider_id} onChange={(e) => set('provider_id', e.target.value)}>
              <option value="">— Select the dentist making this referral —</option>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.name}{p.hpcsa ? ` (HPCSA ${p.hpcsa})` : ' — no HPCSA on file'}</option>)}
            </select>
            {referringProvider && !referringProvider.hpcsa && (
              <p className="mt-2 flex items-center gap-1 text-xs text-amber-600"><AlertTriangle size={12} /> This dentist has no HPCSA number on file — can't send until added.</p>
            )}
            <p className="mt-2 text-xs text-brand-muted">The letter is signed and stamped with this dentist's HPCSA number.</p>
          </div>

          {/* Recipient specialist */}
          <div className="rounded-xl border border-brand-border bg-white p-4" ref={specBox}>
            <h2 className="mb-3 text-sm font-semibold text-brand-ink">Recipient specialist</h2>
            <div className="relative mb-3">
              <label className={label}>Find an existing specialist</label>
              <div className="flex items-center gap-2 rounded-lg border border-brand-border bg-white px-3">
                <Search size={14} className="text-brand-muted" />
                <input className="w-full py-2 text-sm focus:outline-none" placeholder="Search directory…"
                  value={specQuery} onChange={(e) => { setSpecQuery(e.target.value); setShowSpec(true) }} onFocus={() => setShowSpec(true)} />
              </div>
              {showSpec && specResults.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-brand-border bg-white shadow-lg">
                  {specResults.map((s) => (
                    <button key={s.id} type="button" onClick={() => pickSpecialist(s)} className="block w-full px-3 py-2 text-left text-sm hover:bg-brand-surface">
                      <span className="font-medium text-brand-ink">{s.name}</span>
                      <span className="text-brand-muted"> · {s.discipline_label || '—'}{s.email ? ` · ${s.email}` : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className={label}>Name *</label><input className={input} value={form.recipient_name} onChange={(e) => onRecipientNameChange(e.target.value)} placeholder="Dr J. Naidoo" /></div>
              <div><label className={label}>Discipline</label>
                <select className={input} value={form.recipient_discipline} onChange={(e) => set('recipient_discipline', e.target.value)}>
                  <option value="">—</option>
                  {disciplines.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
              </div>
              <div><label className={label}>Practice / rooms</label><input className={input} value={form.recipient_practice} onChange={(e) => set('recipient_practice', e.target.value)} /></div>
              <div><label className={label}>Email (for the specialist's copy)</label><input className={input} type="email" value={form.recipient_email} onChange={(e) => set('recipient_email', e.target.value)} placeholder="rooms@…" /></div>
              <div><label className={label}>Phone</label><input className={input} value={form.recipient_phone} onChange={(e) => set('recipient_phone', e.target.value)} /></div>
              <div><label className={label}>HPCSA (optional)</label><input className={input} value={form.recipient_hpcsa} onChange={(e) => set('recipient_hpcsa', e.target.value)} /></div>
              <div className="sm:col-span-2"><label className={label}>Address</label><input className={input} value={form.recipient_address} onChange={(e) => set('recipient_address', e.target.value)} /></div>
            </div>
            <p className="mt-2 text-xs text-brand-muted">A new recipient is saved to the directory so the next referral to them is one click.</p>
          </div>

          {/* Clinical */}
          <div className="rounded-xl border border-brand-border bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-brand-ink">The referral</h2>
              <button type="button" onClick={draftWithAi} disabled={aiBusy || sent}
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary px-2.5 py-1.5 text-xs font-semibold text-brand-primary hover:bg-brand-surface disabled:opacity-60">
                <Sparkles size={13} /> {aiBusy ? 'Drafting…' : 'Draft with AI'}
              </button>
            </div>
            {aiNote && (
              <div className={cn('rounded-lg p-2.5 text-xs',
                aiNote.kind === 'ai' ? 'bg-emerald-50 text-emerald-700'
                  : aiNote.kind === 'fallback' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700')}>
                {aiNote.text}
              </div>
            )}
            <div><label className={label}>Type</label>
              <select className={input} value={form.referral_type} onChange={(e) => set('referral_type', e.target.value)}>
                {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div><label className={label}>Reason for referral / clinical question *</label>
              <textarea className={input} rows={3} value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="e.g. Please assess and extract impacted 38 (mesio-angular)." />
            </div>
            <div><label className={label}>Clinical summary / findings</label>
              <textarea className={input} rows={3} value={form.clinical_summary} onChange={(e) => set('clinical_summary', e.target.value)} />
            </div>
            <div><label className={label}>Relevant medical &amp; dental history</label>
              <textarea className={input} rows={2} value={form.relevant_history} onChange={(e) => set('relevant_history', e.target.value)} placeholder="Only what's relevant to this referral (POPIA — keep it minimal)." />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className={label}>Teeth / site (FDI)</label><input className={input} value={form.teeth} onChange={(e) => set('teeth', e.target.value)} placeholder="e.g. 38, 48" /></div>
              <div><label className={label}>Priority</label>
                <select className={input} value={form.urgency} onChange={(e) => set('urgency', e.target.value)}>
                  {urgencies.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
                </select>
              </div>
            </div>
            {urgent && <div><label className={label}>Reason it's {form.urgency}</label><input className={input} value={form.urgency_reason} onChange={(e) => set('urgency_reason', e.target.value)} /></div>}
          </div>

          {/* Enclosures */}
          <div className="rounded-xl border border-brand-border bg-white p-4">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-brand-ink"><Paperclip size={14} /> Enclosures</h2>
            {documents.length === 0 && <p className="text-xs text-brand-muted">No documents on file to enclose.</p>}
            <div className="max-h-52 overflow-auto">
              {documents.map((d) => (
                <label key={d.id} className="flex items-center gap-2 border-b border-brand-border/40 py-1.5 text-sm last:border-0">
                  <input type="checkbox" checked={form.attachment_document_ids.includes(d.id)} onChange={(e) => toggleDoc(d.id, e.target.checked)} />
                  <span className="text-brand-ink">{d.title}</span>
                  <span className="ml-auto text-xs text-brand-muted">{d.folder_label} · {d.captured_at}</span>
                </label>
              ))}
            </div>
            {documents.length > 0 && <p className="mt-2 text-xs text-brand-muted">Listed in the letter and attached to the specialist's email.</p>}
          </div>

          {/* POPIA gate */}
          <label className="flex items-start gap-2 rounded-xl border border-brand-border bg-brand-surface/50 p-4 text-sm text-brand-ink">
            <input type="checkbox" className="mt-0.5" checked={form.patient_informed} onChange={(e) => set('patient_informed', e.target.checked)} />
            <span>The patient (or guardian) has been <strong>informed</strong> of this referral — who it's to, why, and what clinical information is shared. <span className="text-brand-muted">Required before sending (POPIA).</span></span>
          </label>

          {!sent && (
            <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-60">
              <FileText size={15} /> {busy ? 'Saving…' : referral ? 'Save changes' : 'Save draft & preview'}
            </button>
          )}
        </div>

        {/* ── Preview + send ───────────────────────────────────── */}
        <div className="space-y-4">
          {referral ? (
            <>
              <div className="overflow-hidden rounded-xl border border-brand-border bg-white">
                <div className="flex items-center justify-between border-b border-brand-border px-4 py-2">
                  <span className="text-sm font-medium text-brand-ink">Letter preview</span>
                  <a href={referral.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-brand-primary hover:underline">Open / print ↗</a>
                </div>
                <iframe title="Referral preview" src={referral.pdf_url} className="h-[560px] w-full" />
              </div>

              {!sent && (
                <div className="rounded-xl border border-brand-border bg-white p-4">
                  {blockers.length > 0 && (
                    <div className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
                      <AlertTriangle size={14} className="mt-0.5" /><span>Before sending: {blockers.join('; ')}. Fix above and Save changes.</span>
                    </div>
                  )}
                  <p className="mb-3 text-xs text-brand-muted">
                    Emails the letter to the <strong>specialist's rooms</strong>; WhatsApps the patient <strong>their copy to carry</strong>. Both file the PDF to the digital file.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => doSend(referral.send_email_path, 'by email to the specialist')} disabled={busy || blockers.length > 0}
                      className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-50">
                      <Mail size={15} /> Send by email
                    </button>
                    <button onClick={() => doSend(referral.send_whatsapp_path, 'to the patient on WhatsApp')} disabled={busy || blockers.length > 0}
                      className="inline-flex items-center gap-2 rounded-lg border border-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary hover:bg-brand-surface disabled:opacity-50">
                      <Send size={15} /> Send on WhatsApp
                    </button>
                  </div>
                  {!patient.has_whatsapp && <p className="mt-2 text-xs text-brand-muted">This patient has no WhatsApp number — email or print instead.</p>}
                </div>
              )}
              {sent && (
                <div className="space-y-3">
                  {referral.outcome_received_at && (
                    <div className="rounded-xl border border-brand-border bg-white p-4 text-sm">
                      <p className="font-semibold text-brand-ink">Specialist outcome · {referral.outcome_received_at}</p>
                      <p className="mt-1 whitespace-pre-wrap text-brand-muted">{referral.outcome_notes}</p>
                    </div>
                  )}
                  <Link href={referral.patient_file_path} className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white">
                    <ArrowLeft size={15} /> Back to the digital file
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-xl border border-dashed border-brand-border bg-white p-8 text-center">
              <FileText size={28} className="mb-2 text-brand-muted" />
              <p className="text-sm text-brand-muted">Fill in the referral and click <strong>Save draft &amp; preview</strong> to generate the letter on the practice letterhead — then send it by email or WhatsApp.</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
